import {
  Box,
  Button,
  Card,
  CardContent,
  DialogActions,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { EventoBarragem } from "../../services/api/company";
import { useState } from "react";
import { Seminario } from "../../services/api/seminarios";
import { SegurancaBarragem } from "../../services/api/segBarragem";
import { Simulado } from "../../services/api/simulados";
import { FileUpload } from "../fileUpload";

interface CardNovoEventoProps {
  eventChange?: EventoBarragem;
  data: Date;
  onAdd: (evento: EventoBarragem, file?: File) => void;
  onClose: () => void;
}

export const CardNovoEvento: React.FC<CardNovoEventoProps> = ({ eventChange, data, onAdd, onClose }) => {
  const { palette } = useTheme();

  const [file, setFile] = useState<File>();
  const [event, setEvent] = useState<EventoBarragem>(eventChange ?? {
    tipo: "seminario",
    evento: {
      id: -1,
      idBarragem: 0,
      status: true,
      local: "",
      data: data,
      responsavel: "",
      dataCadastro: new Date(),
      idUpdate: "",
    }
  });

  let bgcolor = palette.calendario.seminarioCardColor;
  if (event.tipo === "segurancaBarragem") bgcolor = palette.calendario.inspecaoCardColor;
  else if (event.tipo === "simulado") bgcolor = palette.calendario.simuladoCardColor;
  else if (event.tipo === "treinamento") bgcolor = palette.calendario.treinamentoCardColor;

  const handleChange = (ev: SelectChangeEvent) => {
    const value = ev.target.value as ("seminario" | "simulado" | "treinamento" | "segurancaBarragem");
    setEvent({
      tipo: value,
      evento: {
        ...event.evento,
        tipo: value === "segurancaBarragem" ? "outro" : undefined,
        responsavel: "",
        local: value === "seminario" ? "" : undefined,
        participantes: undefined,
        populacao: undefined,
        outro: undefined,
        obs: undefined,
      },
    });
  };

  return <Card
    sx={{
      width: "30em",
      display: "flex",
      bgcolor: bgcolor,
      maxHeight: "75vh",
      flexDirection: "column",
    }}
  >
    <CardContent sx={{
      gap: "0.5em",
      height: "100%",
      display: "flex",
      overflow: "auto",
      flexDirection: "row",
    }}>
      <Box sx={{
        flex: 1,
        gap: "0.5em",
        display: "flex",
        flexDirection: "column",
      }}>

        {/* Data */}
        <div style={{
          gap: "0.5em",
          display: "flex",
          flexDirection: "row",
          alignItems: "end",
          justifyContent: "end",
        }}>
          <Typography fontWeight={650} fontSize={16}>
            {data.toLocaleDateString()}
          </Typography>

          < TextField fullWidth label="Hora" variant="standard"
            value={event.evento.data.getHours()}
            onChange={(ev) => {
              const value = parseInt(ev.target.value);
              if (isNaN(value)) return;
              data.setHours(value);

              setEvent({
                tipo: event.tipo,
                evento: { ...event.evento, data: data }
              });
            }}
          />

          < TextField fullWidth label="Minuto" variant="standard"
            value={event.evento.data.getMinutes()}
            onChange={(ev) => {
              const value = parseInt(ev.target.value);
              if (isNaN(value)) return;
              data.setMinutes(value);

              setEvent({
                tipo: event.tipo,
                evento: { ...event.evento, data: data }
              });
            }}
          />

        </div>
        <Divider />

        {eventChange && <Typography fontWeight={450} fontSize={16}>
          {{
            "simulado": "Simulado",
            "treinamento": "Treinamento",
            "seminario": "Seminário Orientativo",
            "segurancaBarragem": "Inspeção Técnica",
          }[event.tipo]}
        </Typography>}

        {!eventChange && <FormControl fullWidth variant="standard">
          <InputLabel id="demo-simple-select-standard-label">Tipo do Evento</InputLabel>
          <Select
            value={event.tipo}
            label="Tipo do Evento"
            onChange={handleChange}
            id="demo-simple-select-standard"
            labelId="demo-simple-select-standard-label"
          >
            <MenuItem value={"seminario"}>Seminário Orientativo</MenuItem>
            <MenuItem value={"simulado"}>Simulado</MenuItem>
            <MenuItem value={"treinamento"}>Treinamento</MenuItem>
            <MenuItem value={"segurancaBarragem"}>Inspeção Técnica</MenuItem>
          </Select>
        </FormControl>}

        {/* Tipo */}
        {["simulado", "treinamento", "seminario"].includes(event.tipo) ?
          <TextField fullWidth label="Tipo" variant="standard"
            value={event.evento.tipo}
            onChange={(ev) => setEvent({
              tipo: event.tipo,
              evento: { ...event.evento, tipo: ev.target.value }
            })}
          /> :
          <FormControl fullWidth variant="standard">
            <InputLabel>Tipo da Inspeção</InputLabel>
            <Select
              value={event.evento.tipo}
              label="Tipo da Inspeção"
              onChange={(ev) => setEvent({
                tipo: event.tipo,
                evento: { ...event.evento, tipo: ev.target.value }
              })}
            >
              {["EdR", "RISR", "RPSB", "oficio", "outro"].map(
                (val) => <MenuItem key={val} value={val}>{val}</MenuItem>
              )}
            </Select>
          </FormControl>
        }

        {/* Outro */}
        {event.tipo === "segurancaBarragem" && (event.evento as SegurancaBarragem).tipo === "outro" &&
          < TextField fullWidth label="Outro" variant="standard"
            value={(event.evento as SegurancaBarragem).outro}
            onChange={(ev) => setEvent({
              tipo: event.tipo,
              evento: { ...event.evento, responsavel: ev.target.value }
            })}
          />
        }

        {/* Responsável */}
        <TextField fullWidth label="Responsável" variant="standard"
          value={event.evento.responsavel}
          onChange={(ev) => setEvent({
            tipo: event.tipo,
            evento: { ...event.evento, responsavel: ev.target.value }
          })}
        />

        {/* Local */}
        {event.tipo === "seminario" &&
          <TextField fullWidth label="Local" variant="standard"
            value={(event.evento as Seminario).local}
            onChange={(ev) => setEvent({
              tipo: event.tipo,
              evento: { ...event.evento, local: ev.target.value }
            })}
          />
        }

        {/* Participantes */}
        {event.tipo === "seminario" &&
          <TextField fullWidth label="Participantes" variant="standard" type="number"
            value={(event.evento as Seminario).participantes ?? 0}
            onChange={(ev) => {
              const value = parseInt(ev.target.value);
              if (isNaN(value)) return;

              setEvent({
                tipo: event.tipo,
                evento: { ...event.evento, participantes: value }
              });
            }}
          />
        }

        {/* População */}
        {event.tipo === "simulado" &&
          <TextField fullWidth label="População" variant="standard" type="number"
            value={(event.evento as Simulado).populacao ?? 0}
            onChange={(ev) => {
              const value = parseInt(ev.target.value);
              if (isNaN(value)) return;

              setEvent({
                tipo: event.tipo,
                evento: { ...event.evento, populacao: value }
              });
            }}
          />
        }

        {/* Observações */}
        <TextField fullWidth multiline label="Observações" variant="standard"
          value={event.evento.obs}
          onChange={(ev) => setEvent({
            tipo: event.tipo,
            evento: { ...event.evento, obs: ev.target.value }
          })}
        />

        {/* Link do documento */}
        {event.evento.file &&
          <Link onClick={() => {
            if (event.evento.file) window.open(event.evento.file, '_blank');
          }}>
            Acessar Arquivo Atual
          </Link>
        }
      </Box>

      <Box sx={{
        flex: 1,
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
      }}>
        <FileUpload
          multiple
          sx={{ flex: 1 }}
          files={file ? [file] : undefined}
          onAddFile={(files) => setFile(files && files.length > 0 ? files[0] : undefined)}
        />
      </Box>

    </CardContent>

    {/* Botões */}
    <DialogActions>
      <Button color="error" variant="contained" onClick={onClose}>
        Cancelar
      </Button>
      {event &&
        <Button color="success" variant="contained" onClick={() => onAdd(event, file)}>
          {eventChange ? "Modificar" : "Adicionar"}
        </Button>
      }
    </DialogActions>
  </Card>;
};